// The trade sheet and the action window, opened from a card (Oct 28, 2026), plus the staff names the cards
// show ("Set up by Alex Poma"). Shared by the deal page's Trades card and the Builds board's Trades view.
// Oct 29: the sheet's done step Schedule (a signed add-on) closes the sheet and opens Move on that visit.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { fetchTradeById, type Trade, type TradePrice } from "../../lib/trades";
import TradeSheet, { type WorkChoice } from "./TradeSheet";
import TradeActionModal, { type TradeAction } from "./TradeActionModal";

export function useTradeModals(today: string, onChanged: () => void) {
  const [sheet, setSheet] = useState<{ dealId: string; trade?: Trade; price?: TradePrice | null; startWork?: WorkChoice } | null>(null);
  const [act, setAct] = useState<{ trade: Trade; action: TradeAction } | null>(null);

  const openNew = useCallback((dealId: string, startWork?: WorkChoice) => setSheet({ dealId, startWork }), []);
  const openEdit = useCallback((trade: Trade, price: TradePrice | null) => setSheet({ dealId: trade.deal_id, trade, price }), []);
  const openAction = useCallback((trade: Trade, action: TradeAction) => setAct({ trade, action }), []);
  // Schedule from the sheet's done step: the visit as stored now, in the Move window.
  const scheduleFromSheet = useCallback(async (tradeId: string) => {
    try {
      const t = await fetchTradeById(tradeId);
      if (!t) return;
      setSheet(null);
      setAct({ trade: t, action: "move" });
    } catch (e) {
      alert((e as Error).message);
    }
  }, []);

  const element = (
    <>
      {sheet && (
        <TradeSheet
          dealId={sheet.dealId}
          trade={sheet.trade}
          price={sheet.price}
          startWork={sheet.startWork}
          onClose={() => setSheet(null)}
          onSaved={onChanged}
          onSchedule={(id) => { void scheduleFromSheet(id); }}
        />
      )}
      {act && (
        <TradeActionModal trade={act.trade} action={act.action} today={today} onClose={() => setAct(null)} onDone={onChanged} />
      )}
    </>
  );
  return { openNew, openEdit, openAction, element };
}

/** team_roster id -> full name, read once. */
export function useStaffNames(): Record<string, string> {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    let alive = true;
    supabase.from("team_roster").select("id,full_name").then(({ data }) => {
      if (alive && data) setNames(Object.fromEntries((data as Array<{ id: string; full_name: string | null }>).map((r) => [r.id, r.full_name ?? ""])));
    });
    return () => { alive = false; };
  }, []);
  return names;
}
